import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { REALTIME_URL } from 'constants/api';
import { REALTIME_PRICES_ENABLED } from 'constants/flags';
import { getViewerToken } from 'services/client/token';
import { RealtimeClient, type RealtimeStatus } from 'services/client/realtime/RealtimeClient';
import type { PriceQuote, RtPrices } from 'constants/payloads/realtime';

interface PricesContextValue {
  quotes: Record<string, PriceQuote>;
  status: RealtimeStatus;
  register: (symbols: string[]) => void;
  unregister: (symbols: string[]) => void;
  serverNow: () => number; // Date.now() corrected by the hello skew (§17.11.2)
}

const RealtimePricesContext = createContext<PricesContextValue | null>(null);

const MAX_SYMBOLS = 20;
// Mirror the server's symbol grammar (index.ts VALID_SYMBOL). A registrant passing anything
// off-grammar (e.g. `BRK-B`) would otherwise 4400-close the SHARED prices socket for the whole
// tab in a loop (fallback → 11-min retry → same union → 4400) (§17.11.2).
const VALID_SYMBOL = /^[A-Z][A-Z.]{0,5}$/;
// Retry a fresh client just past the RealtimeClient's 10-min fallback cooldown, so a
// tab whose symbol union never drains to zero (a mounted GEX page, Capacitor `/`) heals
// after the cooldown instead of sitting on a dead client forever (§17.8 C11).
const FALLBACK_RETRY_MS = 11 * 60_000;

/**
 * One shared prices socket per tab (spec §11.2). Ref-counts the symbols every
 * consumer wants and drives the socket's live set via `sub`/`unsub` (no
 * reconnect per change). Off entirely when the flag or REALTIME_URL is unset —
 * consumers then fall back to their own data source.
 */
export function RealtimePricesProvider({ children }: { children: React.ReactNode }) {
  const enabled = REALTIME_PRICES_ENABLED && !!REALTIME_URL;
  const [quotes, setQuotes] = useState<Record<string, PriceQuote>>({});
  const [status, setStatus] = useState<RealtimeStatus>('off');

  const refCounts = useRef<Map<string, number>>(new Map());
  const clientRef = useRef<RealtimeClient | null>(null);
  // Debounce union recomputation so a burst of register/unregister coalesces.
  const syncTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Post-cooldown retry timer for the terminal-fallback heal (§17.8 C11).
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // serverTime − Date.now() at the last hello: corrects consumers' freshness checks against a
  // skewed client clock (a slow clock pins a stale spot fresh; a fast one hides a live one) (§17.11.2).
  const serverSkewRef = useRef(0);

  const currentUnion = useCallback((): string[] => {
    const all = Array.from(refCounts.current.keys());
    if (all.length > MAX_SYMBOLS) {
      // The server caps a socket at 20 symbols, so we physically can't stream more
      // on one socket. Don't drop the overflow silently — log which symbols are
      // being starved so it's diagnosable rather than a mystery blank (§17.6 R5).
      console.warn(
        `[realtime-prices] ${
          all.length
        } symbols demanded, streaming first ${MAX_SYMBOLS}; dropping ${all
          .slice(MAX_SYMBOLS)
          .join(',')}`,
      );
    }
    return all.slice(0, MAX_SYMBOLS);
  }, []);

  const mergeQuotes = useCallback((incoming: PriceQuote[]) => {
    if (!incoming.length) return;
    setQuotes((prev) => {
      const next = { ...prev };
      for (const q of incoming) next[q.symbol] = q;
      return next;
    });
  }, []);

  const syncSocket = useCallback(() => {
    if (!enabled) return;
    const union = currentUnion();
    if (union.length === 0) {
      clientRef.current?.stop();
      clientRef.current = null;
      setStatus('off');
      return;
    }
    if (!clientRef.current) {
      const client = new RealtimeClient({
        feed: 'prices',
        baseUrl: REALTIME_URL as string,
        params: { symbols: union },
        getToken: getViewerToken,
        onSnapshot: (body) => mergeQuotes((body as { quotes?: PriceQuote[] }).quotes ?? []),
        onPrices: (p: RtPrices) => mergeQuotes(p.quotes),
        onHello: (h) => {
          serverSkewRef.current = h.serverTime - Date.now();
        },
        onStatus: (s) => {
          setStatus(s);
          if (s === 'fallback') {
            // The client is terminal after a fallback (stopped + visibility detached).
            // Drop our reference so the next syncSocket builds a fresh one whose start()
            // re-checks inCooldown(); keep the last quotes on screen (§17.8 C11).
            const permanent = client.isPermanentFallback();
            clientRef.current = null;
            if (retryTimer.current) clearTimeout(retryTimer.current);
            // A capability-gap fallback (no gzip) will never succeed — don't recreate a client
            // every 11 min for the tab's life; polling owns it permanently (§17.11.2).
            if (permanent) return;
            retryTimer.current = setTimeout(() => {
              retryTimer.current = null;
              syncSocketRef.current();
            }, FALLBACK_RETRY_MS);
          }
        },
      });
      clientRef.current = client;
      client.start();
    } else {
      clientRef.current.updateSymbols(union);
    }
  }, [enabled, currentUnion, mergeQuotes]);
  // Always point at the latest syncSocket so the retry timer can call it without a
  // circular useCallback dependency.
  const syncSocketRef = useRef(syncSocket);
  syncSocketRef.current = syncSocket;

  const scheduleSync = useCallback(() => {
    if (syncTimer.current) clearTimeout(syncTimer.current);
    syncTimer.current = setTimeout(syncSocket, 50);
  }, [syncSocket]);

  // Normalize AND validate at the boundary: uppercase (a lowercase symbol 4400s the SHARED
  // socket, §17.6 R5) then drop anything that fails the server grammar (`BRK-B` etc.) so one
  // bad registrant can't 4400-loop the whole tab's stream (§17.11.2).
  const register = useCallback(
    (symbols: string[]) => {
      for (const s of symbols) {
        const sym = s.toUpperCase();
        if (!VALID_SYMBOL.test(sym)) {
          console.warn(`[realtime-prices] ignoring off-grammar symbol: ${s}`);
          continue;
        }
        refCounts.current.set(sym, (refCounts.current.get(sym) ?? 0) + 1);
      }
      scheduleSync();
    },
    [scheduleSync],
  );

  const unregister = useCallback(
    (symbols: string[]) => {
      for (const s of symbols) {
        const sym = s.toUpperCase();
        const n = (refCounts.current.get(sym) ?? 0) - 1;
        if (n <= 0) refCounts.current.delete(sym);
        else refCounts.current.set(sym, n);
      }
      scheduleSync();
    },
    [scheduleSync],
  );

  useEffect(() => {
    return () => {
      if (syncTimer.current) clearTimeout(syncTimer.current);
      if (retryTimer.current) clearTimeout(retryTimer.current);
      clientRef.current?.stop();
      clientRef.current = null;
    };
  }, []);

  const serverNow = useCallback(() => Date.now() + serverSkewRef.current, []);
  const value = useMemo<PricesContextValue>(
    () => ({ quotes, status, register, unregister, serverNow }),
    [quotes, status, register, unregister, serverNow],
  );

  return <RealtimePricesContext.Provider value={value}>{children}</RealtimePricesContext.Provider>;
}

/**
 * Subscribe to live quotes for a set of symbols. Returns the latest quote per
 * requested symbol (empty until the first tick / off-hours snapshot). No-op when
 * the prices feed is disabled — the returned map just stays empty and callers
 * keep their fallback source.
 */
export function usePriceStream(symbols: string[]): Record<string, PriceQuote> {
  const ctx = useContext(RealtimePricesContext);
  // Uppercase + dedupe so the registration, the quote-map lookup, and the server's
  // symbol set all agree (server symbols are uppercase) (§17.6 R5).
  const key = useMemo(
    () =>
      Array.from(new Set(symbols.map((s) => s.toUpperCase())))
        .sort()
        .join(','),
    [symbols],
  );

  // Depend on the STABLE register/unregister callbacks, not the whole context
  // object — its identity changes on every quote frame, which would otherwise
  // unregister+register (and re-`sub`, billed 20:1) ~once a second (§17.2 P2).
  const register = ctx?.register;
  const unregister = ctx?.unregister;
  useEffect(() => {
    if (!register || !unregister || !key) return;
    const list = key.split(',').filter(Boolean);
    register(list);
    return () => unregister(list);
  }, [key, register, unregister]);

  const quotes = ctx?.quotes;
  return useMemo(() => {
    if (!quotes) return {};
    const out: Record<string, PriceQuote> = {};
    for (const s of key.split(',').filter(Boolean)) {
      if (quotes[s]) out[s] = quotes[s];
    }
    return out;
  }, [quotes, key]);
}

export function useRealtimePricesStatus(): RealtimeStatus {
  return useContext(RealtimePricesContext)?.status ?? 'off';
}

/**
 * Returns a `serverNow()` function: `Date.now()` corrected by the skew measured from the
 * prices `hello.serverTime`, so freshness checks against producer-stamped `ts` don't drift
 * with the client clock. Falls back to plain `Date.now()` when the feed is off (§17.11.2).
 */
export function useRealtimeServerNow(): () => number {
  const ctx = useContext(RealtimePricesContext);
  return ctx?.serverNow ?? Date.now;
}
